import { useRouter } from 'next/router';
import Link from 'next/link';
import PropTypes from 'prop-types';
import moment from 'moment';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Link as DownloadLink,
  Box,
  useTheme,
  useMediaQuery,
  Typography,
  Skeleton,
} from '@mui/material/';
import { WarningAmber as WarningAmberIcon } from '@mui/icons-material';
import * as types from 'src/types';
import { Error, Info } from 'src/components/Banners';
import { loadAgreementPdf } from 'src/api/agreementApi';
import { formatAmountToISKCurrencyString } from 'src/helpers/formatAmountToString';
import { Tag } from '@motusehf/web-ui-component-library/dist';
import { isPendingCancellation, PENDING_CANCELLATION } from 'src/helpers/agreementHelpers';

const MobileAgreementSkeleton = ({ showClaimIds }) => (
  <Box
    sx={{
      p: '20px',
      borderBottom: '1px solid rgba(224, 224, 224, 1)',
    }}
  >
    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
      <Skeleton variant="text" width="40%" height={24} />
      <Skeleton variant="text" width="30%" height={24} />
    </Box>

    {showClaimIds ? (
      <>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Skeleton variant="text" width="35%" height={24} />
          <Skeleton variant="text" width="20%" height={24} />
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Skeleton variant="text" width="35%" height={24} />
          <Skeleton variant="text" width="45%" height={24} />
        </Box>
      </>
    ) : (
      <>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Skeleton variant="text" width="20%" height={24} />
          <Skeleton variant="text" width="40%" height={24} />
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Skeleton variant="text" width="20%" height={24} />
          <Skeleton variant="text" width="30%" height={24} />
        </Box>
      </>
    )}

    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
      <Skeleton variant="text" width="25%" height={24} />
      <Skeleton variant="text" width="35%" height={24} />
    </Box>
    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
      <Skeleton variant="text" width="35%" height={24} />
      <Skeleton variant="text" width="30%" height={24} />
    </Box>
  </Box>
);

const DesktopAgreementSkeleton = ({ showClaimIds, count = 3 }) => (
  <Table>
    <TableHead>
      <TableRow>
        <TableCell sx={{ fontSize: '16px', width: '17.5%' }}>Stofndagur</TableCell>

        {showClaimIds ? (
          <>
            <TableCell sx={{ fontSize: '16px', width: '16%' }}>Fjöldi mála</TableCell>
            <TableCell sx={{ fontSize: '16px', width: '16%' }}>Kröfuhafi</TableCell>
          </>
        ) : (
          <>
            <TableCell sx={{ fontSize: '16px', width: '26%' }}>Pdf</TableCell>
            <TableCell align="right" sx={{ fontSize: '16px', width: '11%' }}>
              Staða
            </TableCell>
          </>
        )}
        <TableCell align="left" sx={{ fontSize: '16px', width: '21%' }}>
          Staða
        </TableCell>
        <TableCell align="right" sx={{ fontSize: '16px' }}>
          Eftirstöðvar
        </TableCell>
      </TableRow>
    </TableHead>
    <TableBody>
      {[...Array(count)].map((_, i) => (
        <TableRow key={`skeleton-${i}`}>
          <TableCell>
            <Skeleton variant="text" width="80%" height={24} />
          </TableCell>

          {showClaimIds ? (
            <>
              <TableCell>
                <Skeleton variant="text" width="60%" height={24} />
              </TableCell>
              <TableCell>
                <Skeleton variant="text" width="70%" height={24} />
              </TableCell>
            </>
          ) : (
            <>
              <TableCell>
                <Skeleton variant="text" width="85%" height={24} />
              </TableCell>
              <TableCell align="right">
                <Skeleton variant="text" width="60%" height={24} sx={{ ml: 'auto' }} />
              </TableCell>
            </>
          )}

          <TableCell align="left">
            <Skeleton variant="text" width="50%" height={24} />
          </TableCell>

          <TableCell align="right">
            <Skeleton variant="text" width="70%" height={24} sx={{ ml: 'auto' }} />
          </TableCell>
        </TableRow>
      ))}
    </TableBody>
  </Table>
);

export default function AgreementsTable({ agreements, showClaimIds = false, error = undefined, isLoading = false }) {
  const router = useRouter();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  if (error !== undefined) {
    return <Error error={error} />;
  }

  if (isLoading) {
    if (isMobile) {
      return (
        <>
          {[...Array(3)].map((_, i) => (
            <MobileAgreementSkeleton key={`mobile-skeleton-${i}`} showClaimIds={showClaimIds} />
          ))}
        </>
      );
    }
    return <DesktopAgreementSkeleton showClaimIds={showClaimIds} count={3} />;
  }

  if (agreements.length === 0) {
    return <Info message="Fann engin samkomulög um greiðsludreifingu." />;
  }

  const getAgreementStatus = (line) => {
    if (line?.isActive) return 'Virkt';
    if (line?.closed) return 'Lokið';
    if (line?.defaulted) return 'Gjaldfellt';
    if (line?.paid) return 'Greitt';
    if (line?.status === 'PendingApprovalInternal') return 'PendingApprovalInternal';
    if (line?.status === 'PendingApprovalPayer') return 'PendingApprovalPayer';
    return '';
  };

  const getNextDueDate = (installments) => {
    if (!installments || installments.length === 0) {
      return null;
    }

    const unpaidInstallments = installments
      .filter((installment) => !installment.isPaid)
      .sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate));

    return unpaidInstallments.length > 0 ? unpaidInstallments[0].dueDate : null;
  };

  const formatNextDueDate = (line) => {
    const nextDueDate = getNextDueDate(line.installments);
    if (nextDueDate) {
      return moment(nextDueDate).format('DD.MM.YYYY');
    }
    return 'Enginn gjalddagi til';
  };

  const claimLink = (claimInfos, isMobile) => (
    <span>
      {claimInfos.length}
      {isMobile ? '' : ' mál'}
    </span>
  );

  const pdfLink = (line) => (
    <DownloadLink
      component="button"
      variant="body2"
      onClick={(e) => {
        e.stopPropagation();
        loadAgreementPdf(line.pdfName, line.id);
      }}
      sx={{
        p: '3px',
        my: '-3px',
        display: 'inline-block',
        '@media print': {
          pl: '0px',
        },
      }}
    >
      {line.pdfName}
    </DownloadLink>
  );

  const handleRowClick = (e, agreementId) => {
    if (e.button !== 0 || e.ctrlKey || e.metaKey) {
      return;
    }
    e.preventDefault();
    router.push(`/greidslusamkomulag/${agreementId}`);
  };

  const renderStatusContent = (line, isMobile = false) => {
    const status = getAgreementStatus(line);
    const isPendingCancellationStatus = isPendingCancellation(line);

    if (isPendingCancellationStatus) {
      return (
        <Box sx={{ flexGrow: 1, textAlign: isMobile ? 'right' : 'left' }}>
          <Tag label={PENDING_CANCELLATION.TAG_LABEL} color={PENDING_CANCELLATION.TAG_COLOR} fontWeight={600} />
        </Box>
      );
    }

    if (status === 'PendingApprovalInternal') {
      return (
        <Box sx={{ flexGrow: 1, textAlign: isMobile ? 'right' : 'left' }}>
          <Tag label="Ósamþykkt – í skoðun" color="#F7D17A" fontWeight={600} />
        </Box>
      );
    }
    if (status === 'PendingApprovalPayer') {
      return (
        <Box sx={{ flexGrow: 1, textAlign: isMobile ? 'right' : 'left' }}>
          <Tag label="Bíður samþykkis þíns" color="#337FD6" fontWeight={600} />
        </Box>
      );
    }

    return (
      <Box sx={{ display: 'flex', alignItems: isMobile ? 'center' : 'flex-start' }}>
        <Typography
          variant="body1"
          sx={{ fontSize: '16px', color: line?.isAgreementOverdue ? '#B30000' : 'inherit', fontWeight: '400' }}
        >
          {line?.numberOfPaidInstallments} / {line?.numberOfInstallments}
        </Typography>
        {line?.isAgreementOverdue && (
          <>
            <WarningAmberIcon sx={{ color: '#B30000', ml: '8px' }} />
            <Typography sx={{ color: '#B30000', ml: '8px', fontSize: '16px' }}>Vanskil</Typography>
          </>
        )}
      </Box>
    );
  };
  if (isMobile) {
    return agreements.map((line, i) => (
      <Box
        key={`attachment${i}`}
        onClick={(e) => handleRowClick(e, line.id)}
        sx={{
          p: '20px',
          cursor: 'pointer',
          borderBottom: '1px solid rgba(224, 224, 224, 1)',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="body1" sx={{ fontSize: '16px', fontWeight: '600' }} color="text.main">
            Stofndagur
          </Typography>
          <Typography variant="body1" sx={{ fontSize: '16px' }} color="text.main">
            {formatNextDueDate(line)}
          </Typography>
        </Box>

        {showClaimIds ? (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="body1" sx={{ fontSize: '16px', fontWeight: '600' }} color="text.main">
                Fjöldi mála
              </Typography>
              <Typography variant="body1" sx={{ fontSize: '16px' }} color="text.main">
                {claimLink(line.claimInfos, isMobile)}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="body1" sx={{ fontSize: '16px', fontWeight: '600' }} color="text.main">
                Kröfuhafi
              </Typography>
              <Typography variant="body1" sx={{ fontSize: '16px' }} color="text.main">
                {(() => {
                  const uniqueClaimants = [...new Set(line.claimInfos.map((c) => c.claimantName))];
                  return uniqueClaimants.length === 1
                    ? uniqueClaimants[0]
                    : `${line.claimInfos.length}${isMobile ? '' : ' kröfuhafar'}`;
                })()}
              </Typography>
            </Box>
          </>
        ) : (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Pdf
              </Typography>
              <Typography>{line.pdfName && pdfLink(line)}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Staða
              </Typography>
              <Typography sx={{ textAlign: 'right' }}>{getAgreementStatus(line)}</Typography>
            </Box>
          </>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="body1" sx={{ fontSize: '16px', fontWeight: '600' }} color="text.main">
            Staða
          </Typography>
          {renderStatusContent(line, true)}
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="body1" sx={{ fontSize: '16px', fontWeight: '600' }} color="text.main">
            Eftirstöðvar
          </Typography>
          <Typography variant="body1" sx={{ fontSize: '16px' }} color="text.main">
            {formatAmountToISKCurrencyString(line.remainingTotalAmountDue)}
          </Typography>
        </Box>
      </Box>
    ));
  }
  return (
    <Table>
      <TableHead>
        <TableRow>
          <TableCell sx={{ fontSize: '16px', width: '17.5%' }}>Stofndagur</TableCell>

          {showClaimIds ? (
            <>
              <TableCell sx={{ fontSize: '16px', width: '16%' }}>Fjöldi mála</TableCell>
              <TableCell sx={{ fontSize: '16px', width: '16%' }}>Kröfuhafi</TableCell>
            </>
          ) : (
            <>
              <TableCell sx={{ fontSize: '16px', width: '26%' }}>Pdf</TableCell>
              <TableCell align="right" sx={{ fontSize: '16px', width: '11%' }}>
                Staða
              </TableCell>
            </>
          )}
          <TableCell align="left" sx={{ fontSize: '16px', width: '21%' }}>
            Staða
          </TableCell>
          <TableCell align="right" sx={{ fontSize: '16px' }}>
            Eftirstöðvar
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {agreements.map((line, i) => (
          <Link href={`/greidslusamkomulag/${line?.id}`} passHref legacyBehavior key={`attachment${i}`}>
            <TableRow
              component="a"
              onClick={(e) => handleRowClick(e, line?.id)}
              hover
              sx={{
                cursor: 'pointer',
                textDecoration: 'none',
                color: 'inherit',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                },
              }}
            >
              <TableCell sx={{ fontSize: '16px' }}>{formatNextDueDate(line)}</TableCell>

              {showClaimIds ? (
                <>
                  <TableCell sx={{ fontSize: '16px' }}>{claimLink(line?.claimInfos, isMobile)}</TableCell>
                  <TableCell sx={{ fontSize: '16px' }}>
                    {(() => {
                      const uniqueClaimants = [...new Set(line.claimInfos.map((c) => c.claimantName))];
                      return uniqueClaimants.length === 1
                        ? uniqueClaimants[0]
                        : `${line.claimInfos.length}${isMobile ? '' : ' kröfuhafar'}`;
                    })()}
                  </TableCell>
                </>
              ) : (
                <>
                  <TableCell sx={{ fontSize: '16px' }}>{line?.pdfName && pdfLink(line)}</TableCell>
                  <TableCell align="right" sx={{ fontSize: '16px' }}>
                    {getAgreementStatus(line)}
                  </TableCell>
                </>
              )}

              <TableCell align="left" sx={{ fontSize: '16px' }}>
                {renderStatusContent(line)}
              </TableCell>

              <TableCell align="right" sx={{ fontSize: '16px' }}>
                {formatAmountToISKCurrencyString(line?.remainingTotalAmountDue)}
              </TableCell>
            </TableRow>
          </Link>
        ))}
      </TableBody>
    </Table>
  );
}

AgreementsTable.propTypes = {
  agreements: PropTypes.arrayOf(types.agreement).isRequired,
  showClaimIds: PropTypes.bool,
  showLinkToAgreement: PropTypes.bool,
  error: PropTypes.object,
  isLoading: PropTypes.bool,
};
